// src/components/layouts/confluence.js
import React from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  SvgIcon,
  Link as MuiLink,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import ElephantLogo from "../../assets/confluenceElephant.png";

// --- Font imports (ONLY affect this file) ---
// (Uncomment if you chose Option A)
import "@fontsource/cinzel/700.css";
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/600.css";
import "@fontsource/montserrat/800.css";

// TikTok icon
const TikTokIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 48 48">
    <path d="M33.9 16.5a12 12 0 0 1-5.2-3.5 12 12 0 0 1-1.5-2.4c-.3-.6-.5-1.2-.6-1.7h-.1v20.6a7.1 7.1 0 1 1-7.1-7.1c.6 0 1.2.06 1.8.18v5.04a2.8 2.8 0 1 0-1.8 2.64V7.1h6.1c.2.9.5 1.9 1 2.8a9.9 9.9 0 0 0 3.4 3.7 10 10 0 0 0 3.9 1.7v6.1c-1.78-.27-3.5-.86-4.9-1.9z" />
  </SvgIcon>
);

const ConfluenceLayout = ({
  businessCard,
  companyConfig,
  handleAddToContacts,
}) => {
  const P = companyConfig?.primaryColor || "#d4a24d"; // gold
  const D = companyConfig?.paperBackground || "#2b2116"; // deep brown
  const TXT = companyConfig?.textColor || "#FFFFFF";
  const bgImg = companyConfig?.backgroundImage;
  const socials = companyConfig?.socialIcons || {};

  const title = "CONFLUENCE"; // lock to design treatment
  const tagline = "TOURS & SAFARI";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: D,
        color: TXT,
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      {/* Top Banner */}
      <Box
        sx={{
          height: 280,
          position: "relative",
          bgcolor: P,
          backgroundImage: bgImg ? `url(${bgImg})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Header (elephant + brand) */}
        <Box
          sx={{
            position: "absolute",
            top: 18,
            left: 0,
            right: 0,
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            gap: 0.5,
            textAlign: "center",
          }}
        >
          <img
            src={ElephantLogo}
            alt="Elephant Logo"
            style={{ width: "64px", height: "auto", marginBottom: "6px" }}
          />
          <Typography
            sx={{
              fontFamily: "'Cinzel', serif",
              fontWeight: 700,
              fontSize: { xs: "1.9rem", sm: "2.1rem" },
              letterSpacing: ".08em",
              color: D,
              textTransform: "uppercase",
              lineHeight: 1.05,
            }}
          >
            {title}
          </Typography>

          {/* Tagline with flanking lines */}
          <Box
            sx={{
              mt: 0.2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box sx={{ height: 2, width: 36, bgcolor: D, opacity: 0.9 }} />
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                color: D,
                letterSpacing: ".25em",
                fontSize: ".95rem",
                textTransform: "uppercase",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {tagline}
            </Typography>
            <Box sx={{ height: 2, width: 36, bgcolor: D, opacity: 0.9 }} />
          </Box>
        </Box>

        {/* Avatar */}
        <Avatar
          alt={businessCard.name}
          src={businessCard.profilePicURL}
          sx={{
            width: 196, // slightly larger for presence
            height: 196,
            border: "6px solid #cf9b4e",
            boxShadow: "0 8px 18px rgba(0,0,0,.35)",
            position: "absolute",
            left: "50%",
            bottom: -98, // tuned to sit snug on arc edge
            transform: "translateX(-50%)",
            zIndex: 2,
          }}
        />
      </Box>

      {/* Details Section */}
      <Box
        sx={{
          flex: 1,
          px: 3,
          pt: "104px", // keeps name tight under avatar
          maxWidth: 440,
          width: "100%",
          mx: "auto",
          textAlign: "center",
        }}
      >
        {/* Name */}
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 800,
            fontSize: "1.9rem",
            letterSpacing: ".02em",
            color: P,
            textTransform: "uppercase",
            mb: 0.4,
            mt: "-8px",
          }}
        >
          {businessCard.name}
        </Typography>

        {/* Title */}
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 600,
            letterSpacing: ".25em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,.9)",
            fontSize: ".95rem",
            mb: 2.2,
          }}
        >
          {businessCard.position || "Safari Guide"}
        </Typography>

        {/* Contact Info */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            alignItems: "center",
            columnGap: 2,
            rowGap: 1.7,
            textAlign: "left",
            mx: "auto",
            maxWidth: 420,
            mb: 2.4,
          }}
        >
          {/* Phone */}
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              background: P,
              display: "grid",
              placeItems: "center",
            }}
          >
            <PhoneIcon />
          </Box>
          <Button
            href={businessCard.phone ? `tel:${businessCard.phone}` : undefined}
            disabled={!businessCard.phone}
            sx={{
              justifyContent: "flex-start",
              color: "rgba(255,255,255,.92)",
              fontSize: "1.05rem",
              textTransform: "none",
              p: 0,
              "&:hover": { background: "transparent", color: "#fff" },
            }}
          >
            {businessCard.phone || "Phone unavailable"}
          </Button>

          {/* Email */}
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              background: P,
              display: "grid",
              placeItems: "center",
            }}
          >
            <EmailIcon />
          </Box>
          <Button
            href={
              businessCard.email ? `mailto:${businessCard.email}` : undefined
            }
            disabled={!businessCard.email}
            sx={{
              justifyContent: "flex-start",
              color: "rgba(255,255,255,.92)",
              fontSize: "1.05rem",
              textTransform: "none",
              p: 0,
              "&:hover": { background: "transparent", color: "#fff" },
              overflowWrap: "anywhere",
            }}
          >
            {businessCard.email || "Email unavailable"}
          </Button>

          {/* Website */}
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              background: P,
              display: "grid",
              placeItems: "center",
            }}
          >
            <LanguageIcon />
          </Box>
          <MuiLink
            href={companyConfig.website || "#"}
            target="_blank"
            underline="none"
            sx={{
              color: "rgba(255,255,255,.92)",
              fontSize: "1.05rem",
              "&:hover": { color: "#fff" },
              overflowWrap: "anywhere",
            }}
          >
            {companyConfig.website?.replace(/^https?:\/\/(www\.)?/, "") ||
              "Website"}
          </MuiLink>
        </Box>

        {/* Socials */}
        <Box
          sx={{ display: "flex", gap: 1.5, justifyContent: "center", mb: 3 }}
        >
          {socials?.facebook && (
            <IconButton
              href={socials.facebook}
              target="_blank"
              sx={{ color: P }}
            >
              <FacebookIcon />
            </IconButton>
          )}
          {socials?.instagram && (
            <IconButton
              href={socials.instagram}
              target="_blank"
              sx={{ color: P }}
            >
              <InstagramIcon />
            </IconButton>
          )}
          {socials?.tiktok && (
            <IconButton href={socials.tiktok} target="_blank" sx={{ color: P }}>
              <TikTokIcon />
            </IconButton>
          )}
        </Box>

        {/* Save Contact */}
        <Button
          variant="contained"
          sx={{
            backgroundColor: P,
            color: "#1d1208",
            fontWeight: 800,
            borderRadius: "28px",
            px: 4,
            py: 1.3,
            "&:hover": { backgroundColor: "#bd8d3f" },
          }}
          onClick={handleAddToContacts}
        >
          Save Contact
        </Button>
      </Box>
    </Box>
  );
};

export default ConfluenceLayout;
